import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, AlertTriangle, Users, Activity, TrendingUp, TrendingDown, 
  Clock, Eye, AlertOctagon, CheckCircle, BarChart3, Target, Zap,
  Globe, Server, Lock, Cpu
} from 'lucide-react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import api from '../services/api';

// Smooth count-up animation hook — enterprise easing, no bounce
const useCountUp = (target, duration = 1800) => {
  const [value, setValue] = useState(0);
  const startTime = useRef(null);
  const rafId = useRef(null);

  const easeOutCubic = useCallback((t) => 1 - Math.pow(1 - t, 3), []);

  useEffect(() => {
    if (target === undefined || target === null) return;
    const end = Number(target);
    if (isNaN(end)) return;

    startTime.current = performance.now();

    const tick = (now) => {
      const elapsed = now - startTime.current;
      const progress = Math.min(elapsed / duration, 1);
      setValue(Math.round(easeOutCubic(progress) * end));
      if (progress < 1) {
        rafId.current = requestAnimationFrame(tick);
      }
    };

    rafId.current = requestAnimationFrame(tick);
    return () => { if (rafId.current) cancelAnimationFrame(rafId.current); };
  }, [target, duration, easeOutCubic]);

  return value;
};

const ExecutiveDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/executive/dashboard');
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch executive dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Animated counters — hooks must be before any return
  const animPosture = useCountUp(data?.posture?.posture_score, 1800);
  const animCritical = useCountUp(data?.posture?.metrics?.critical_violations, 1600);
  const animOpen = useCountUp(data?.posture?.metrics?.open_violations, 1700);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#9CA3AF]">Loading dashboard...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-[#9CA3AF]">Failed to load dashboard data</p>
      </div>
    );
  }

  const { posture, incidents, discovery, trends, top_risky_agents, top_violations } = data;

  const getPostureColor = (level) => {
    switch (level) {
      case 'strong': return '#34C759';
      case 'moderate': return '#FFCC00';
      case 'weak': return '#FF6961';
      default: return '#FF3B30';
    }
  };

  const trendData = [
    { day: 'Mon', identities: Math.max(trends.total_identities - 6, 0), violations: 3 },
    { day: 'Tue', identities: Math.max(trends.total_identities - 5, 0), violations: 5 },
    { day: 'Wed', identities: Math.max(trends.total_identities - 4, 0), violations: 4 },
    { day: 'Thu', identities: Math.max(trends.total_identities - 3, 0), violations: 6 },
    { day: 'Fri', identities: Math.max(trends.total_identities - 2, 0), violations: 5 },
    { day: 'Sat', identities: Math.max(trends.total_identities - 1, 0), violations: 2 },
    { day: 'Sun', identities: trends.total_identities, violations: posture.metrics.open_violations },
  ];

  // Calculate blast radius count (high impact agents)
  const blastRadiusCount = top_risky_agents.filter(a => a.risk_score > 60).length;
  
  // Calculate environments at risk from discovery data
  const envsAtRisk = discovery?.environments_at_risk || top_risky_agents.filter(a => a.risk_score > 50).length;

  return (
    <div data-testid="executive-dashboard" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-3xl font-bold text-[#F9FAFB]" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Security Overview
          </h1>
          <p className="text-[#6B7280] text-sm">
            Last updated: {new Date().toLocaleString()}
          </p>
        </div>
        <Link to="/dashboard/inventory">
          <button className="secondary-btn text-sm">
            View All Identities
          </button>
        </Link>
      </div>

      {/* Primary Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* Posture Score - Primary */}
        <div className="enterprise-card p-5 border-l-4" style={{ borderLeftColor: getPostureColor(posture.posture_level) }}>
          <div className="flex items-center justify-between mb-1">
            <Shield className="w-5 h-5" style={{ color: getPostureColor(posture.posture_level) }} />
            <span className={`text-[10px] font-mono uppercase px-2 py-0.5 ${
              posture.posture_level === 'strong' ? 'text-[#34C759] bg-[#34C759]/10' :
              posture.posture_level === 'moderate' ? 'text-[#FFCC00] bg-[#FFCC00]/10' :
              'text-[#FF3B30] bg-[#FF3B30]/10'
            }`}>
              {posture.posture_level}
            </span>
          </div>
          <div className="text-3xl font-bold font-mono" style={{ color: getPostureColor(posture.posture_level) }}>
            {animPosture}
          </div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mt-1">Posture Score</div>
        </div>

        {/* Critical Violations */}
        <div className="enterprise-card p-5">
          <div className="flex items-center justify-between mb-1">
            <AlertOctagon className="w-5 h-5 text-[#FF3B30]" />
          </div>
          <div className="text-3xl font-bold font-mono text-[#FF3B30]">
            {animCritical}
          </div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mt-1">Critical Violations</div>
        </div>

        {/* Total Open Violations */}
        <div className="enterprise-card p-5">
          <div className="flex items-center justify-between mb-1">
            <AlertTriangle className="w-5 h-5 text-[#FFCC00]" />
          </div>
          <div className="text-3xl font-bold font-mono text-[#FFCC00]">
            {animOpen}
          </div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mt-1">Open Violations</div>
        </div>

        {/* Orphaned Identities */}
        <div className="enterprise-card p-5">
          <div className="flex items-center justify-between mb-1">
            <Users className="w-5 h-5 text-[#FF6961]" />
          </div>
          <div className="text-3xl font-bold font-mono text-[#FF6961]">
            {posture.metrics.agents_without_owner}
          </div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mt-1">Orphaned Identities</div>
        </div>

        {/* Blast Radius High */}
        <div className="enterprise-card p-5">
          <div className="flex items-center justify-between mb-1">
            <Zap className="w-5 h-5 text-[#0066FF]" />
          </div>
          <div className="text-3xl font-bold font-mono text-[#0066FF]">
            {blastRadiusCount}
          </div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mt-1">High Blast Radius</div>
        </div>

        {/* Environments at Risk */}
        <div className="enterprise-card p-5">
          <div className="flex items-center justify-between mb-1">
            <Globe className="w-5 h-5 text-[#9CA3AF]" />
          </div>
          <div className="text-3xl font-bold font-mono text-[#F9FAFB]">
            {envsAtRisk}
          </div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mt-1">Envs at Risk</div>
        </div>
      </div>

      {/* Second Row - Posture Breakdown & Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Posture Components */}
        <div className="enterprise-card p-6">
          <h2 className="text-sm font-bold text-[#9CA3AF] uppercase tracking-wider mb-4">
            Posture Breakdown
          </h2>
          <div className="space-y-4">
            {[
              { label: 'Identity Ownership', value: posture.components.ownership_score, color: '#0066FF' },
              { label: 'Violation Management', value: posture.components.violation_score, color: '#34C759' },
              { label: 'Policy Coverage', value: posture.components.policy_coverage, color: '#FFCC00' },
            ].map((item, i) => (
              <div key={i}>
                <div className="flex justify-between mb-1">
                  <span className="text-xs text-[#9CA3AF]">{item.label}</span>
                  <span className="text-xs font-mono text-[#F9FAFB]">{item.value}%</span>
                </div>
                <div className="w-full h-1.5 bg-[#1A1A1A]">
                  <div 
                    className="h-full transition-all duration-500"
                    style={{ width: `${item.value}%`, backgroundColor: item.color }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-[#27272A]/40">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-xl font-mono font-bold text-[#F9FAFB]">{posture.metrics.total_agents}</div>
                <div className="text-[10px] text-[#6B7280] uppercase">Total Identities</div>
              </div>
              <div>
                <div className="text-xl font-mono font-bold text-[#34C759]">{posture.metrics.active_agents}</div>
                <div className="text-[10px] text-[#6B7280] uppercase">Active</div>
              </div>
            </div>
          </div>
        </div>

        {/* Weekly Trend Chart */}
        <div className="enterprise-card p-6 lg:col-span-2">
          <h2 className="text-sm font-bold text-[#9CA3AF] uppercase tracking-wider mb-4">
            7-Day Trend
          </h2>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="day" stroke="#6B7280" fontSize={10} />
              <YAxis stroke="#6B7280" fontSize={10} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#121212', 
                  border: '1px solid #27272A',
                  borderRadius: '0',
                  fontSize: '12px'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="identities" 
                stroke="#0066FF" 
                fill="#0066FF20" 
                name="Identities"
              />
              <Area 
                type="monotone" 
                dataKey="violations" 
                stroke="#FF3B30" 
                fill="#FF3B3020" 
                name="Violations"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Third Row - Top Risks & Incidents */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Risky Agents */}
        <div className="enterprise-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-[#9CA3AF] uppercase tracking-wider">
              Highest Risk Identities
            </h2>
            <Link to="/dashboard/inventory" className="text-[#0066FF] text-xs hover:text-[#3385FF]">
              View All →
            </Link>
          </div>
          {top_risky_agents.length > 0 ? (
            <div className="space-y-2">
              {top_risky_agents.map((agent, i) => (
                <Link 
                  key={agent.id} 
                  to={`/dashboard/agents/${agent.id}`}
                  className="flex items-center justify-between p-3 bg-[#1A1A1A] hover:bg-[#222] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-[#6B7280] font-mono text-xs w-4">#{i + 1}</span>
                    <div>
                      <div className="text-[#F9FAFB] font-mono text-sm">{agent.name}</div>
                      <div className="text-[10px] text-[#6B7280]">
                        {agent.owner || 'No Owner Assigned'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] font-mono uppercase px-2 py-0.5 ${
                      agent.risk_level === 'critical' ? 'text-[#FF3B30] bg-[#FF3B30]/10' : 'text-[#FF6961] bg-[#FF6961]/10'
                    }`}>
                      {agent.risk_level}
                    </span>
                    <span className="font-mono text-lg font-bold text-[#FF3B30] w-8 text-right">
                      {agent.risk_score}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[#6B7280]">
              <CheckCircle className="w-10 h-10 mx-auto mb-2 text-[#34C759]" />
              <p className="text-sm">No high-risk identities</p>
            </div>
          )}
        </div>

        {/* Incident Status */}
        <div className="enterprise-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-[#9CA3AF] uppercase tracking-wider">
              Incident Status
            </h2>
            <Link to="/dashboard/incidents" className="text-[#0066FF] text-xs hover:text-[#3385FF]">
              View Queue →
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 bg-[#1A1A1A]">
              <div className="text-2xl font-mono font-bold text-[#F9FAFB]">{incidents.open_incidents}</div>
              <div className="text-[10px] text-[#6B7280] uppercase">Open</div>
            </div>
            <div className="p-3 bg-[#1A1A1A]">
              <div className="text-2xl font-mono font-bold text-[#FF3B30]">{incidents.by_severity?.critical || 0}</div>
              <div className="text-[10px] text-[#6B7280] uppercase">Critical</div>
            </div>
            <div className="p-3 bg-[#1A1A1A]">
              <div className="text-2xl font-mono font-bold text-[#FF6961]">{incidents.by_severity?.high || 0}</div>
              <div className="text-[10px] text-[#6B7280] uppercase">High</div>
            </div>
            <div className="p-3 bg-[#1A1A1A]">
              <div className="text-2xl font-mono font-bold text-[#34C759]">
                {incidents.mttr_minutes ? `${incidents.mttr_minutes}m` : '—'}
              </div>
              <div className="text-[10px] text-[#6B7280] uppercase">Avg MTTR</div>
            </div>
          </div>
          {incidents.sla_breached > 0 && (
            <div className="p-3 bg-[#FF3B30]/10 border border-[#FF3B30]/30">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#FF3B30]" />
                <span className="text-[#FF3B30] text-sm">
                  {incidents.sla_breached} incidents breached SLA
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fourth Row - Violations by Type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="enterprise-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-[#9CA3AF] uppercase tracking-wider">
              Top Violation Types
            </h2>
            <Link to="/dashboard/violations" className="text-[#0066FF] text-xs hover:text-[#3385FF]">
              View All →
            </Link>
          </div>
          {top_violations.length > 0 ? (
            <div className="space-y-2">
              {top_violations.map((v, i) => (
                <div key={i} className="flex items-center justify-between p-2">
                  <span className="text-[#9CA3AF] text-sm">
                    {v._id?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                  <span className="font-mono text-[#F9FAFB] bg-[#1A1A1A] px-3 py-1 text-sm">
                    {v.count}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[#6B7280]">
              <CheckCircle className="w-10 h-10 mx-auto mb-2 text-[#34C759]" />
              <p className="text-sm">No violations detected</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="enterprise-card p-6">
          <h2 className="text-sm font-bold text-[#9CA3AF] uppercase tracking-wider mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <Link to="/dashboard/killswitch" className="p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/30 hover:bg-[#FF3B30]/20 transition-colors">
              <Zap className="w-6 h-6 text-[#FF3B30] mb-2" />
              <div className="text-sm font-medium text-[#F9FAFB]">Kill Switch</div>
              <div className="text-[10px] text-[#6B7280]">Contain threats</div>
            </Link>
            <Link to="/dashboard/graph" className="p-4 bg-[#0066FF]/10 border border-[#0066FF]/30 hover:bg-[#0066FF]/20 transition-colors">
              <Eye className="w-6 h-6 text-[#0066FF] mb-2" />
              <div className="text-sm font-medium text-[#F9FAFB]">Permission Graph</div>
              <div className="text-[10px] text-[#6B7280]">Visualize access</div>
            </Link>
            <Link to="/dashboard/policies" className="p-4 bg-[#FFCC00]/10 border border-[#FFCC00]/30 hover:bg-[#FFCC00]/20 transition-colors">
              <Shield className="w-6 h-6 text-[#FFCC00] mb-2" />
              <div className="text-sm font-medium text-[#F9FAFB]">Policies</div>
              <div className="text-[10px] text-[#6B7280]">Manage rules</div>
            </Link>
            <Link to="/dashboard/audit" className="p-4 bg-[#34C759]/10 border border-[#34C759]/30 hover:bg-[#34C759]/20 transition-colors">
              <Clock className="w-6 h-6 text-[#34C759] mb-2" />
              <div className="text-sm font-medium text-[#F9FAFB]">Audit Log</div>
              <div className="text-[10px] text-[#6B7280]">Review activity</div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
